import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ApiError } from "@/services/BaseService";
import {
  useStartGuestDeletion,
  useStartHotelDeletion,
  useStartTenantDeletion,
  useStartUserDeletion,
} from "@/hooks/useDataDeletion";
import {
  CONFIRM_FIELD_BY_TARGET,
  DeletionJob,
  DeletionTargetDescriptor,
  DeletionTargetType,
  StartDeletionDto,
} from "@/types/data-deletion";
import { DeletionConfirmBody } from "./DeletionConfirmBody";
import { DeletionJobStatusView } from "./DeletionJobStatusView";

const TENANT_SCOPED_TARGETS: ReadonlySet<DeletionTargetType> = new Set([
  DeletionTargetType.HOTEL,
  DeletionTargetType.GUEST,
]);

type FlowPhase = "confirm" | "status";

interface UseDeletionFlowOptions {
  /** Fires once a started job reaches COMPLETED — invalidate lists, navigate. */
  onCompleted?: (job: DeletionJob) => void;
}

export interface DeletionFlow {
  /** Open the type-to-confirm modal for a target. */
  startDeletion: (descriptor: DeletionTargetDescriptor) => void;
  /** Open the status panel for an already-running/finished job. */
  viewJob: (jobId: string) => void;
  /** Render once in the host component. */
  dialogs: React.ReactNode;
}

/**
 * Wires the full GDPR deletion UX for a screen: a single dialog that starts at
 * the type-to-confirm step and transitions to the live status panel on `202`.
 */
export const useDeletionFlow = (
  options: UseDeletionFlowOptions = {},
): DeletionFlow => {
  const { t } = useTranslation();
  const { onCompleted } = options;

  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState<FlowPhase>("confirm");
  const [descriptor, setDescriptor] = useState<DeletionTargetDescriptor | null>(
    null,
  );
  const [jobId, setJobId] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const tenantMutation = useStartTenantDeletion();
  const hotelMutation = useStartHotelDeletion();
  const userMutation = useStartUserDeletion();
  const guestMutation = useStartGuestDeletion();

  const mutationByType = useMemo(
    () => ({
      [DeletionTargetType.TENANT]: tenantMutation,
      [DeletionTargetType.HOTEL]: hotelMutation,
      [DeletionTargetType.USER]: userMutation,
      [DeletionTargetType.GUEST]: guestMutation,
    }),
    [tenantMutation, hotelMutation, userMutation, guestMutation],
  );

  const activeMutation = descriptor ? mutationByType[descriptor.type] : null;

  const close = useCallback(() => {
    setOpen(false);
    setDescriptor(null);
    setJobId(null);
    setServerError(null);
    setPhase("confirm");
  }, []);

  const startDeletion = useCallback((next: DeletionTargetDescriptor) => {
    setDescriptor(next);
    setJobId(null);
    setServerError(null);
    setPhase("confirm");
    setOpen(true);
  }, []);

  const viewJob = useCallback((nextJobId: string) => {
    setDescriptor(null);
    setJobId(nextJobId);
    setServerError(null);
    setPhase("status");
    setOpen(true);
  }, []);

  const handleStartError = useCallback(
    (error: unknown, target: DeletionTargetDescriptor) => {
      const targetWord = t(`dataDeletion.targetWord.${target.type}`);

      if (error instanceof ApiError) {
        if (error.statusCode === 409) {
          toast.error(
            t("dataDeletion.job.toast.inFlight", { target: targetWord }),
          );
          close();
          return;
        }
        if (error.statusCode === 404) {
          toast.error(t("dataDeletion.job.toast.notFound"));
          close();
          return;
        }
        setServerError(
          error.message ||
            t("dataDeletion.confirm.mismatch", {
              what: t(
                `dataDeletion.confirm.what.${CONFIRM_FIELD_BY_TARGET[target.type]}`,
              ),
            }),
        );
        return;
      }

      setServerError(
        error instanceof Error
          ? error.message
          : t("dataDeletion.confirm.mismatch", {
              what: t(
                `dataDeletion.confirm.what.${CONFIRM_FIELD_BY_TARGET[target.type]}`,
              ),
            }),
      );
    },
    [close, t],
  );

  const handleConfirm = useCallback(
    async (confirmation: string) => {
      if (!descriptor) {
        return;
      }
      const mutation = mutationByType[descriptor.type];
      const dto: StartDeletionDto = { confirmation };
      if (TENANT_SCOPED_TARGETS.has(descriptor.type)) {
        dto.tenantId = descriptor.tenantId;
      }
      if (descriptor.type === DeletionTargetType.GUEST && descriptor.mode) {
        dto.mode = descriptor.mode;
      }

      try {
        const job = await mutation.mutateAsync({ id: descriptor.id, dto });
        toast.success(t("dataDeletion.job.toast.started"));
        setJobId(job.id);
        setServerError(null);
        setPhase("status");
      } catch (error) {
        handleStartError(error, descriptor);
      }
    },
    [descriptor, handleStartError, mutationByType, t],
  );

  const dialogs = (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          close();
        }
      }}
    >
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto">
        {phase === "confirm" && descriptor ? (
          <DeletionConfirmBody
            descriptor={descriptor}
            isPending={activeMutation?.isPending ?? false}
            serverError={serverError}
            onConfirm={(value) => void handleConfirm(value)}
            onCancel={close}
          />
        ) : null}
        {phase === "status" && jobId ? (
          <DeletionJobStatusView
            key={jobId}
            jobId={jobId}
            onCompleted={onCompleted}
            onClose={close}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );

  return { startDeletion, viewJob, dialogs };
};
